// Lógica pura de Pinterest en el front: qué aviso llegó del popup, qué decirle a la persona según
// el código de error y qué imágenes son de Pinterest. Sin red ni DOM, para probarla con vitest.
// Los efectos viven en `pinterestConexion.ts`.
//
// Todo Pinterest vive en atlas-backend (ADR-2026-10-23-pinterest-guardados-como-referencia): el
// front nunca habla con Pinterest ni lee las tablas `pinterest_*`. La conexión es de la PERSONA,
// no de la marca. Nada de lo que trae se guarda: los tableros y los pines se piden cada vez.

/** Se dispara en `window` al conectar o desconectar. */
export const EVENTO_CAMBIO_DE_PINTEREST = 'atlas:pinterest-cambio';

/** Clave donde `pinterest-conectado.html` deja el aviso por si se perdió el `opener`. */
export const CLAVE_AVISO_PINTEREST = 'atlas:pinterest-conectado';

/** El `type` que manda `pinterest-conectado.html`. */
export const TIPO_AVISO_PINTEREST = 'pinterest-conectado';

/** Un aviso guardado hace más que esto es de un intento viejo: se ignora. */
export const VIDA_DEL_AVISO_MS = 15 * 60 * 1000;

/** Lo que tarda como máximo alguien en autorizar (el state del backend vence a los 10 min). */
export const ESPERA_MAXIMA_MS = 10 * 60 * 1000;

export type AvisoDePinterest = { reclamo: string } | { error: string };

/**
 * Lee lo que mandó `pinterest-conectado.html` (por postMessage o por localStorage). Devuelve null
 * si no es un aviso nuestro, si está vencido o si el reclamo no tiene la forma esperada (el backend
 * lo genera con 32 bytes en base64url).
 */
export function leerAvisoDePinterest(data: unknown, ahora = Date.now()): AvisoDePinterest | null {
  let d = data;
  if (typeof d === 'string') {
    try {
      d = JSON.parse(d);
    } catch {
      return null;
    }
  }
  if (!d || typeof d !== 'object') return null;
  const a = d as { type?: unknown; reclamo?: unknown; error?: unknown; t?: unknown };
  if (a.type !== TIPO_AVISO_PINTEREST) return null;
  if (typeof a.t === 'number' && ahora - a.t > VIDA_DEL_AVISO_MS) return null;
  if (typeof a.error === 'string' && /^[a-z_]{1,40}$/.test(a.error)) return { error: a.error };
  if (typeof a.reclamo === 'string' && /^[A-Za-z0-9_-]{32,200}$/.test(a.reclamo)) return { reclamo: a.reclamo };
  return null;
}

/** Qué decirle a la persona según el código que devolvió el backend o el popup. */
export function mensajeDeErrorDePinterest(code: string): string {
  switch (code) {
    case 'cancelado':
      return 'Cancelaste la conexión en Pinterest. Cuando quieras, probá de nuevo.';
    case 'vencido':
    case 'reclamo_invalido':
    case 'tiempo':
      return 'La conexión tardó demasiado y se venció. Probá de nuevo.';
    case 'reconectar':
      return 'Pinterest pidió volver a conectar tu cuenta. Tocá Conectar de nuevo.';
    case 'sin_conexion':
      return 'Todavía no conectaste tu Pinterest.';
    case 'no_encontrado':
      return 'Ese tablero ya no existe en Pinterest.';
    case 'ocupado':
      return 'Pinterest está recibiendo muchos pedidos. Probá de nuevo en un rato.';
    case 'demora':
      return 'Pinterest tardó en contestar. Probá de nuevo en un rato.';
    case 'no_disponible':
    case 'no_configurado':
    case 'origen_no_permitido':
      return 'La conexión con Pinterest todavía no está disponible desde acá.';
    case 'red':
      return 'No pudimos comunicarnos con Atlas. Revisá tu conexión a internet y probá de nuevo.';
    case 'sin_sesion':
      return 'Tu sesión venció. Volvé a iniciar sesión y probá de nuevo.';
    case 'popup_bloqueado':
      return 'Tu navegador bloqueó la ventana de Pinterest. Permití las ventanas emergentes para Atlas y probá de nuevo.';
    case 'ventana_cerrada':
      return 'Se cerró la ventana de Pinterest antes de terminar. Probá de nuevo.';
    case 'app_instalada':
      return 'Desde la app instalada Pinterest no puede volver a Atlas. Abrí Atlas en Safari, conectalo ahí una vez y después lo vas a ver también acá.';
    default:
      return 'No pudimos traer tu Pinterest. Probá de nuevo en un rato.';
  }
}

/** El error de Pinterest: `code` estable para decidir, `message` para mostrar. */
export class ErrorDePinterest extends Error {
  constructor(readonly code: string) {
    super(mensajeDeErrorDePinterest(code));
    this.name = 'ErrorDePinterest';
  }
}

export interface EstadoDePinterest {
  /** El backend tiene Pinterest prendido para esta cuenta (si no, el botón no se dibuja). */
  disponible: boolean;
  conectada: boolean;
  estado: 'activa' | 'reconectar' | null;
  username: string | null;
  /** La pill Pinterest de "Recrear con plantillas" se muestra (mientras se prueba, sólo para
   *  los dev testers; ADR-2026-10-29-pinterest-en-recrear-con-plantillas). */
  enRecrear: boolean;
  /** `true` = no pudimos preguntar. */
  error: boolean;
}

/**
 * Traduce la respuesta de `GET /v1/pinterest/estado`. Un 404 es Pinterest apagado para esta
 * cuenta (sin las credenciales de la app, o mientras se prueba sólo con dev testers): no es un
 * error, es "no está disponible", y el botón no aparece.
 */
export function estadoDesdeRespuesta(status: number, cuerpo: unknown): EstadoDePinterest {
  const nada = { conectada: false, estado: null, username: null, enRecrear: false } as const;
  if (status === 404) return { disponible: false, ...nada, error: false };
  if (status < 200 || status >= 300 || !cuerpo || typeof cuerpo !== 'object') {
    return { disponible: false, ...nada, error: true };
  }
  const d = cuerpo as { conectada?: unknown; estado?: unknown; username?: unknown; en_recrear?: unknown };
  const estado = d.estado === 'activa' || d.estado === 'reconectar' ? d.estado : null;
  return {
    disponible: true,
    conectada: d.conectada === true,
    estado,
    username: typeof d.username === 'string' && d.username ? d.username : null,
    // Un backend que todavía no lo manda la deja oculta.
    enRecrear: d.en_recrear === true,
    error: false,
  };
}

/** Saca el código de error estable de una respuesta del backend (`{ ok:false, error }`). */
export function codigoDeError(status: number, cuerpo: unknown): string {
  if (status === 404) {
    const e = cuerpo && typeof cuerpo === 'object' ? (cuerpo as { error?: unknown }).error : null;
    // Un 404 con código nuestro dice qué pasó; uno sin código es la ruta apagada.
    return e === 'no_encontrado' || e === 'reclamo_invalido' ? e : 'no_disponible';
  }
  if (status === 401) return 'sin_sesion';
  const e = cuerpo && typeof cuerpo === 'object' ? (cuerpo as { error?: unknown }).error : null;
  return typeof e === 'string' && e ? e : 'error';
}

/**
 * ¿Es una imagen de Pinterest? (`i.pinimg.com`, https, sin puerto ni usuario). Es lo único de afuera
 * que el backend acepta como referencia, además de nuestro Storage.
 */
export function esImagenDePinterest(url: unknown): url is string {
  if (typeof url !== 'string') return false;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && u.hostname === 'i.pinimg.com' && !u.username && !u.password && !u.port;
  } catch {
    return false;
  }
}

/** Lo que ve la persona si un pin llega a un VIDEO, que sólo toma fotos de Atlas. */
export const PIN_NO_ES_CUADRO =
  'Las imágenes de Pinterest sirven de inspiración para imágenes, no para videos. Generá primero una imagen con el pin y animá esa, o subí la foto.';

/** Un tablero, como lo devuelve el backend. */
export interface TableroDePinterest {
  id: string;
  nombre: string;
  secreto: boolean;
  cantidad: number | null;
  portada: string | null;
}

/** Un pin, como lo devuelve el backend: la miniatura para la grilla y la imagen grande para generar. */
export interface PinDePinterest {
  id: string;
  titulo: string | null;
  tablero_id: string | null;
  miniatura: string;
  imagen: string;
}

/** Se queda sólo con lo que tiene forma de tablero, y con portadas que sean de Pinterest. */
export function tablerosDesdeRespuesta(cuerpo: unknown): { tableros: TableroDePinterest[]; siguiente: string | null } {
  const d = (cuerpo && typeof cuerpo === 'object' ? cuerpo : {}) as { tableros?: unknown; siguiente?: unknown };
  const tableros = (Array.isArray(d.tableros) ? d.tableros : []).flatMap((t): TableroDePinterest[] => {
    const x = t as Partial<TableroDePinterest>;
    if (typeof x?.id !== 'string' || typeof x.nombre !== 'string') return [];
    return [
      {
        id: x.id,
        nombre: x.nombre,
        secreto: x.secreto === true,
        cantidad: typeof x.cantidad === 'number' ? x.cantidad : null,
        portada: esImagenDePinterest(x.portada) ? x.portada : null,
      },
    ];
  });
  return { tableros, siguiente: typeof d.siguiente === 'string' && d.siguiente ? d.siguiente : null };
}

/** Se queda sólo con los pines cuyas dos imágenes son de Pinterest. */
export function pinesDesdeRespuesta(cuerpo: unknown): { pines: PinDePinterest[]; siguiente: string | null } {
  const d = (cuerpo && typeof cuerpo === 'object' ? cuerpo : {}) as { pines?: unknown; siguiente?: unknown };
  const pines = (Array.isArray(d.pines) ? d.pines : []).flatMap((p): PinDePinterest[] => {
    const x = p as Partial<PinDePinterest>;
    if (typeof x?.id !== 'string' || !esImagenDePinterest(x.miniatura) || !esImagenDePinterest(x.imagen)) return [];
    return [
      {
        id: x.id,
        titulo: typeof x.titulo === 'string' && x.titulo.trim() ? x.titulo.trim() : null,
        tablero_id: typeof x.tablero_id === 'string' ? x.tablero_id : null,
        miniatura: x.miniatura,
        imagen: x.imagen,
      },
    ];
  });
  return { pines, siguiente: typeof d.siguiente === 'string' && d.siguiente ? d.siguiente : null };
}
